/**
 * Quest Service
 * Handles all quest-related API calls
 */

import apiClient from "../client";
import { ENDPOINTS } from "../endpoints";

/**
 * Fetch all available quests
 * @returns {Promise<Array>} - List of all quests
 */
export const getQuests = async () => {
  try {
    // Request all active quests with a high limit (100 is the max allowed by backend)
    const response = await apiClient.get(`${ENDPOINTS.QUEST.LIST}?isActive=true&page=1&limit=100`);
    // Extract quests from response - handle different response structures
    return response.quests || response.data?.quests || response.data || response || [];
  } catch (error) {
    console.error('[QuestService] Error fetching quests:', error.message);
    throw error;
  }
};

/**
 * Fetch completed quests for a user
 * @param {string} userAddress - User's wallet address
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 5)
 * @returns {Promise<Object>} - User's completed quests
 */
export const getUserQuests = async (userAddress, params = {}) => {
  const { page = 1, limit = 5 } = params;

  try {
    const response = await apiClient.get(
      `${ENDPOINTS.QUEST.BY_USER}?page=${page}&limit=${limit}`.replace(
        ":userAddress",
        userAddress,
      ),
      {},
    );

    return {
      quests: Array.isArray(response.quests) ? response.quests : [],
      message: response.message,
    };
  } catch (error) {
    return {
      quests: [],
      message: "Failed to fetch quests",
    };
  }
};

/**
 * Check if a user has completed a specific quest
 * @param {string} questId - Quest ID
 * @param {string} userAddress - User's wallet address
 * @returns {Promise<boolean>} - Whether the quest is completed
 */
export const checkQuestCompletion = async (questId, userAddress) => {
  try {
    const response = await apiClient.get(
      ENDPOINTS.QUEST.USER_QUEST_STATUS(questId, userAddress),
    );
    
    return response.isCompleted || false;
  } catch (error) {
    console.error('[QuestService] Error checking quest completion:', error.message);
    return false;
  }
};

const questService = {
  getQuests,
  getUserQuests,
  checkQuestCompletion,
};

export default questService;
